import { Variants, Transition } from "framer-motion";

// ============================================================================
// ANIMATION SYSTEM - Best Practices for Restaurant Menu
// ============================================================================
// Uses modern whileInView approach with optimized easing and durations
// Follows: https://www.framer.com/motion/scroll-animations/

// Easing curves - smooth, professional feel
const easeOut: [number, number, number, number] = [0.22, 1, 0.36, 1]; // Apple-style ease
const easeIn: [number, number, number, number] = [0.42, 0, 1, 1];
const easeInOut: [number, number, number, number] = [0.42, 0, 0.58, 1];
const spring: Transition = { type: "spring", stiffness: 100, damping: 15 };

// ============================================================================
// CARD ANIMATIONS - Scroll-triggered fade-in for product cards
// ============================================================================

/**
 * Card reveal animation - smooth fade up with scale
 * Use with whileInView for scroll-triggered animations
 */
export const cardReveal: Variants = {
  hidden: {
    opacity: 0,
    y: 24,
    scale: 0.96,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: easeOut,
    },
  },
  exit: {
    opacity: 0,
    y: 12,
    scale: 0.98,
    transition: { duration: 0.25, ease: easeIn },
  },
};

/**
 * Staggered card container - for grid layouts
 * Apply to parent, children get staggered animations
 */
export const cardGridContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      staggerChildren: 0.05,
      staggerDirection: -1,
    },
  },
};

/**
 * Card item for use within cardGridContainer
 */
export const cardItem: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: easeOut,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.2, ease: easeIn },
  },
};

// ============================================================================
// PAGE & SECTION ANIMATIONS
// ============================================================================

type BatchCustom =
  | number
  | {
      batchIndex?: number;
      itemIndex?: number;
      baseDelay?: number;
      batchDelay?: number;
      itemDelay?: number;
      exitDelay?: number;
    };

const resolveDelay = (
  custom: BatchCustom,
  defaults?: { baseDelay?: number; batchDelay?: number; itemDelay?: number },
) => {
  if (typeof custom === "number") {
    return { delay: custom };
  }

  const {
    batchIndex = 0,
    itemIndex = 0,
    baseDelay = defaults?.baseDelay ?? 0,
    batchDelay = defaults?.batchDelay ?? 0.25,
    itemDelay = defaults?.itemDelay ?? 0.05,
  } = custom ?? {};

  return {
    delay: baseDelay + batchIndex * batchDelay + itemIndex * itemDelay,
    exitDelay: custom?.exitDelay ?? 0,
  };
};

export const pageFade: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.35, ease: easeInOut },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.25, ease: easeIn },
  },
};

export const pageBackdrop: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.35, ease: easeInOut, delay: 0.05 },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.25, ease: easeIn },
  },
};

export const sidebarSequence: Variants = {
  hidden: { opacity: 0, x: -24 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, ease: easeOut, delay: 0.1 },
  },
  exit: {
    opacity: 0,
    x: -28,
    transition: { duration: 0.25, ease: easeIn },
  },
};

export const searchBarSequence: Variants = {
  hidden: { opacity: 0, y: -16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: easeOut, delay: 0.15 },
  },
  exit: {
    opacity: 0,
    y: -14,
    transition: { duration: 0.25, ease: easeIn },
  },
};

export const fadeSlideUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: (custom: BatchCustom = 0) => {
    const { delay } = resolveDelay(custom);
    return {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: easeOut, delay },
    };
  },
  exit: (custom: BatchCustom = 0) => ({
    opacity: 0,
    y: 16,
    transition: {
      duration: 0.25,
      ease: easeIn,
      delay: typeof custom === "number" ? 0 : (custom?.exitDelay ?? 0),
    },
  }),
};

export const batchFadeSlideUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: easeOut },
  },
  exit: {
    opacity: 0,
    y: 14,
    transition: { duration: 0.25, ease: easeIn },
  },
};

export const fadeSlideDown: Variants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: easeOut },
  },
  exit: {
    opacity: 0,
    y: -14,
    transition: { duration: 0.25, ease: easeIn },
  },
};

export const fadeSlideFromLeft: Variants = {
  hidden: { opacity: 0, x: -32 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, ease: easeOut },
  },
  exit: {
    opacity: 0,
    x: -24,
    transition: { duration: 0.25, ease: easeIn },
  },
};

export const fadeSlideFromRight: Variants = {
  hidden: { opacity: 0, x: 32 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, ease: easeOut },
  },
  exit: {
    opacity: 0,
    x: 24,
    transition: { duration: 0.25, ease: easeIn },
  },
};

export const gridReveal: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: easeOut,
      staggerChildren: 0.06,
      delayChildren: 0.02,
    },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.25, ease: easeIn },
  },
};

export const menuStagger: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.15,
    },
  },
  exit: {
    transition: {
      staggerChildren: 0.06,
      when: "afterChildren",
    },
  },
};

export const floatingCartSequence: Variants = {
  hidden: { opacity: 0, x: 24, scale: 0.98 },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: { duration: 0.35, ease: easeOut, delay: 0.25 },
  },
  exit: {
    opacity: 0,
    x: 28,
    scale: 0.97,
    transition: { duration: 0.25, ease: easeIn },
  },
};

export const staggerContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.04,
      delayChildren: 0.05,
    },
  },
  exit: {
    transition: {
      staggerChildren: 0.03,
      when: "afterChildren",
    },
  },
};

// Export spring for external use
export { spring, easeOut, easeIn, easeInOut };
